import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { AuthService } from '../services/auth.service';

// Arreglo de rutas/URLs donde se omite la verificación
const EXCLUDED_URLS = ['/auth/refresh', '/auth/login', '/auth/logout', 's3.amazonaws.com'];

/**
 * Interceptor que se encarga de:
 * 1. Verificar si el token está a punto de expirar o expiró antes de hacer la petición;
 *    si es así, refresca el token.
 * 2. Agregar el token (Bearer) en las cabeceras.
 * 3. Manejar errores 401, si ocurrieran.
 */
@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private router = inject(Router);
  private authService = inject(AuthService);

  /**
   * Método principal que intercepta todas las peticiones HTTP
   * @param request Petición HTTP original
   * @param next Siguiente manejador en la cadena de interceptores
   * @returns Observable<HttpEvent<unknown>>
   */
  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    // Verificar si la URL debe excluirse
    if (this.isExcludedUrl(request)) {
      // No agregamos token ni hacemos verificación de expiración
      return next.handle(request);
    }

    // Si la URL NO está excluida, seguimos con la lógica normal

    // Obtenemos el token (Access Token JWT) del localStorage (o de donde lo guardes)
    let token = this.authService.getAccessToken();

    // Si no hay token, redirigimos a login y enviamos la petición sin cabecera Authorization
    if (!token) {
      this.router.navigate(['/auth/login']);
      return next.handle(request);
    }

    // Verificamos si el token está expirado o por expirar
    const isExpiringSoon = this.authService.isTokenExpiringSoon(token);
    if (isExpiringSoon) {
      // REFRESCAMOS ANTES DE ENVIAR LA PETICIÓN
      // convertimos la Promesa/Observable de refreshToken en un Observable que
      // se encadene con la petición original
      return this.authService.handleRefreshToken(token).pipe(
        switchMap((newToken: string) => {
          this.authService.setAccessToken(newToken);
          token = newToken;

          const authReq = request.clone({
            headers: request.headers.set('Authorization', `Bearer ${token}`),
          });
          return next.handle(authReq);
        }),
        catchError((err) => {
          // Refresh falló — limpiar sesión sin disparar MSAL redirect
          this.authService.clearSession();
          this.router.navigate(['/auth/login']);
          return throwError(() => err);
        }),
      );
    }

    // Si NO está próximo a expirar, simplemente enviamos la petición con el token actual
    const authReq = request.clone({
      headers: request.headers.set('Authorization', `Bearer ${token}`),
    });

    // Continuamos con el flujo de la petición
    return next.handle(authReq).pipe(
      catchError((error) => {
        // Si el status es 401 (por ej., token inválido o error del backend)
        if (error instanceof HttpErrorResponse && error.status === 401) {
          this.authService.clearSession();
          this.router.navigate(['/auth/login']);
          return throwError(() => error);
        }
        return throwError(() => error);
      }),
    );
  }

  /**
   * Revisa si la petición actual coincide con una de las URLs excluidas.
   * @param request Petición HTTP
   */
  private isExcludedUrl(request: HttpRequest<unknown>): boolean {
    return EXCLUDED_URLS.some((excluded) => request.url.includes(excluded));
  }
}
